import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingIndicator } from '@airia/ui';
import { ChatMessage, ChatMarkdown, useSubagentActivity, SubagentBlock, ToolCallBlock, ThinkingBlock, WebSearchBlock, } from '@airia/ui/chat';
import { customMarkdownComponents } from './custom-markdown';
export function AddinAssistantMessage({ events, pipelineId, isStreaming, typewriterText, markdownProps, }) {
    const { segments, hasActivitySegments } = useSubagentActivity({
        events,
        pipelineId,
    });
    const renderBlockMarkdown = useCallback((content) => _jsx(ChatMarkdown, { content: content }), []);
    // `components` maps agent-emitted picker tags
    // (airiaMessageButton / Dropdown / Checkboxes / Radios) to our React
    // implementations. @airia/ui's customTagsPlugin handles extraction
    // when `enableCustomTags: true`. Caller-supplied components win via merge.
    const streamingMarkdownProps = useMemo(() => ({
        enableCustomTags: true,
        disableLoadingIndicator: true,
        ...markdownProps,
        components: { ...customMarkdownComponents, ...(markdownProps?.components ?? {}) },
        isStreaming: true,
        streamingId: 'addin-streaming-active',
    }), [markdownProps]);
    const completedMarkdownProps = useMemo(() => ({
        ...markdownProps,
        enableCustomTags: true,
        components: { ...customMarkdownComponents, ...(markdownProps?.components ?? {}) },
    }), [markdownProps]);
    // Idle spinner logic — mirrors platform's ChatAssistantMessage
    const [showIdleIndicator, setShowIdleIndicator] = useState(false);
    const idleTimerRef = useRef(undefined);
    const contentFingerprintRef = useRef(null);
    const hasReceivedContentRef = useRef(false);
    const contentFingerprint = useMemo(() => {
        let fp = '';
        for (const s of segments) {
            fp += s.id;
            fp += s.type === 'text' ? s.content.length : '';
            fp += 'isActive' in s ? (s.isActive ? '1' : '0') : '';
            fp += '|';
        }
        fp += typewriterText.length;
        return fp;
    }, [segments, typewriterText]);
    const hasContent = segments.length > 0 || typewriterText.length > 0;
    useEffect(() => {
        if (!isStreaming) {
            setShowIdleIndicator(false);
            clearTimeout(idleTimerRef.current);
            contentFingerprintRef.current = null;
            hasReceivedContentRef.current = false;
            return;
        }
        // Show spinner immediately at the start before any content arrives
        if (!hasContent && !hasReceivedContentRef.current) {
            setShowIdleIndicator(true);
            return;
        }
        hasReceivedContentRef.current = true;
        if (contentFingerprint === contentFingerprintRef.current)
            return;
        contentFingerprintRef.current = contentFingerprint;
        setShowIdleIndicator(false);
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            setShowIdleIndicator(true);
        }, 2000);
    }, [isStreaming, contentFingerprint, hasContent]);
    useEffect(() => () => clearTimeout(idleTimerRef.current), []);
    const hasActiveBlock = segments.some((s) => s.type !== 'text' && 'isActive' in s && s.isActive);
    // No activity segments — fall back to typewriter text or idle spinner
    if (!hasActivitySegments) {
        if (typewriterText) {
            return (_jsx(ChatMessage, { messageRole: "assistant", variant: "omni", content: typewriterText, renderMarkdown: true, isStreaming: isStreaming, markdownProps: streamingMarkdownProps }));
        }
        // No text yet — show idle spinner while streaming
        if (isStreaming && showIdleIndicator) {
            return (_jsx("div", { className: "py-2", children: _jsx("div", { className: "flex items-center gap-2 px-0.5", children: _jsx(LoadingIndicator, { size: "xs" }) }) }));
        }
        return null;
    }
    // Activity segments present — render blocks
    return (_jsx("div", { className: "w-full flex flex-col gap-4", children: _jsxs("div", { children: [segments.map((segment, index) => {
                    if (segment.type === 'text') {
                        const isLast = index === segments.length - 1;
                        return (_jsx("div", { children: _jsx(ChatMessage, { messageRole: "assistant", variant: "omni", content: segment.content, renderMarkdown: true, isStreaming: isLast && isStreaming, markdownProps: isLast && isStreaming
                                    ? streamingMarkdownProps
                                    : completedMarkdownProps }) }, segment.id));
                    }
                    if (segment.type === 'subagent') {
                        return (_jsx("div", { className: "py-2", children: _jsx(SubagentBlock, { segment: segment, renderMarkdown: renderBlockMarkdown }) }, segment.id));
                    }
                    if (segment.type === 'thinking') {
                        return (_jsx("div", { className: "py-2", children: _jsx(ThinkingBlock, { segment: segment, renderMarkdown: renderBlockMarkdown }) }, segment.id));
                    }
                    if (segment.type === 'websearch') {
                        return (_jsx("div", { className: "py-2", children: _jsx(WebSearchBlock, { segment: segment }) }, segment.id));
                    }
                    if (segment.type === 'toolcall') {
                        return (_jsx("div", { className: "py-2", children: _jsx(ToolCallBlock, { segment: segment, renderMarkdown: renderBlockMarkdown }) }, segment.id));
                    }
                    return null;
                }), segments.every((s) => s.type !== 'text') && typewriterText && (_jsx(ChatMessage, { messageRole: "assistant", variant: "omni", content: typewriterText, renderMarkdown: true, isStreaming: isStreaming, markdownProps: streamingMarkdownProps })), isStreaming && showIdleIndicator && !hasActiveBlock && (_jsx("div", { className: "py-2", children: _jsx("div", { className: "flex items-center gap-2 px-0.5", children: _jsx(LoadingIndicator, { size: "xs" }) }) }))] }) }));
}
